import type { DateRange } from 'react-day-picker'
import type { FieldViewProps } from '../../types'
import type DateRangeController from './Controller'

import React, { useMemo } from 'react'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { format, isValid } from 'date-fns'
import { enUS as defaultLocale } from 'date-fns/locale/en-US'

import {
	DatePicker,
	DatePickerCalendar,
	DatePickerContent,
	DatePickerInput,
} from '@/shared/components/ui/date-picker'

import { Operators } from '../../constants'
import { getUTCDate } from '../../utils/utils'

type Props = FieldViewProps<DateRangeController> & { showTimePicker?: boolean }
export default function DateRangeView({
	dateFormat,
	locale,
	onChange,
	storedValue: _storedValue,
	invalidMessage,
	showTimePicker,
	disabled,
}: Props & { dateFormat: 'EU' | 'US' }) {
	const [storedValue, setStoredValue] = useControllableState({
		prop: _storedValue,
		defaultProp: {
			operation: Operators.DATE_BETWEEN,
			value: [],
		},
		onChange,
	})
	const value = useMemo(
		() => ({
			from: storedValue.value?.[0]
				? getUTCDate(new Date(storedValue.value?.[0]))
				: undefined,
			to: storedValue.value?.[1]
				? getUTCDate(new Date(storedValue.value?.[1]))
				: undefined,
		}),
		[storedValue]
	)

	const handleDateRangeChange = (range?: DateRange) => {
		if (!range) {
			setStoredValue({
				operation: Operators.DATE_BETWEEN,
				value: [],
			})
		} else {
			if (showTimePicker) {
				setStoredValue({
					...storedValue,
					value: [range.from, range.to].filter(
						(date) => !!date && isValid(date)
					),
				})
			} else {
				setStoredValue({
					...storedValue,
					value: [range.from, range.to]
						.filter((date) => !!date && isValid(date))
						.map((date) => format(date!, 'yyyy-MM-dd')),
				})
			}
		}
	}

	const isInvalid = Boolean(invalidMessage)

	return (
		<div className='col-span-3 flex items-center'>
			<DatePicker
				key='range'
				required
				mode='range'
				value={value}
				onValueChange={handleDateRangeChange}
				invalid={isInvalid}
				disabled={disabled}
			>
				<DatePickerInput
					format={dateFormat}
					data-testid='query-builder-value-date'
				/>
				<DatePickerContent>
					<DatePickerCalendar
						numberOfMonths={2}
						locale={locale?.getDateFnsLocalization?.() ?? defaultLocale}
						captionLayout='dropdown'
					/>
				</DatePickerContent>
			</DatePicker>
		</div>
	)
}
